<template>
  <b-container fluid>
    <h1 class="m-3">
      {{ title }}
    </h1>
    <commonActions key="month-picker" :data-ready="dataReadyTables" :filename-prefix="title"
      :date-range-to-force="dateRangeToForce" :date-picker-format="datePickerFormat"
      @commonFiltersUpdated="commonFiltersUpdated($event)" @clickDownloadPdf="downloadPdf($event)"
      @clickDownloadExcel="downloadExcel($event)" @clickDownloadCsv="downloadCsv($event)" />

    <table-renderer v-for="data of tablesData" :key="data.id" ref="tableRenderers" :table-number="data.tableNumber"
      :data-table="data.dataTable" :dimension-fields="dimensionFields" :measure-fields="data.measureColumnsTables"
      :table-header="data.tableHeader" :data-ready="dataReadyTables" :group-headers="groupHeaders"
      :totals-data="data.totalsData" />
  </b-container>
</template>

<script>
import Tools from "@/utils/tools";
import TableTools from "@/utils/tableTools";
import paths from "@/router/pathConstants";
import TableRenderer from "@/components/TableRenderer.vue";
import commonActions from "@/components/pages/commonActions";
import dimensionEnum from "@/model/dimensionEnum";
import mesureEnum from "@/model/mesureEnum";
import { dateFormatEnum } from "@/filter/dateFormat";
import { OperationsExpired } from "@/router/routeNames";
import { calculatedColumnsEnum, columnTypeEnum } from "@/model/columnsEnums";
import abortMixin from "@/mixins/abortMixin";

export default {
  name: OperationsExpired,
  components: {
    TableRenderer,
    commonActions,
  },
  mixins: [abortMixin],
  data() {
    return {
      commonFilters: [],
      timeDimension: null,
      siteValues: [],
      datePickerFormat: dateFormatEnum.DATE_SHORT_FORMAT,
      dataReadyTables: false,
      tablesData: [],
      dimensionFields: [],
      measureFields: [],
      groupHeaders: [],
    };
  },
  computed: {
    title() {
      return this.$t("title.operations_expired");
    },
    dateRangeToForce() {
      // Période par défaut : le mois précédent, au complet
      const lastMonth = Tools.BusinessDateHelper.getTodaysBusinessDate().subtract(
        1,
        "months"
      );
      return [
        lastMonth.startOf("month").format(this.datePickerFormat),
        lastMonth.endOf("month").format(this.datePickerFormat),
      ];
    },
  },
  mounted() {
    this.initDisplayTable();
  },
  methods: {
    initDisplayTable() {
      this.dimensionFields = TableTools.Helper.constructColumnTable([
        dimensionEnum.DATE,
      ]);

      this.groupHeaders = [
        { nbOfMeasures: 4, label: this.$t("group.expiration") },
        { nbOfMeasures: 3, label: this.$t("group.runningTotalsDifference") },
      ];
    },
    commonFiltersUpdated(value) {
      this.commonFilters = value.filters;
      this.timeDimension = value.timeDimension || null;
      this.siteValues = value.siteValues;
      this.updateTable();
    },
    async updateTable() {
      try {
        const { signal } = this.resetAbortController();
        this.dataReadyTables = false;
        this.tablesData = [];

        // Création des tableaux par site sans les données
        this.siteValues.forEach((site, id) => {
          var table = {
            tableNumber: id.toString() + "-1",
            tableNumberBySite: "1",
            tableHeader: site.label,
            tableHeaderId: site.id,
            dataTable: [],
            totalsData: {},
            dimensionColumnsTables: null,
            measureColumnsTables: null,
            groupHeaders: this.groupHeaders,
            customTableClass: "table-width-xl export-xxl",
          };
          this.tablesData.push(table);
        });

        // Construction de la requête Query
        const query = {
          measures: [
            mesureEnum.PURGEHISTORY_TOKEN,
            mesureEnum.PURGEHISTORY_FREE_TOKEN,
            mesureEnum.PURGEHISTORY_TOKEN_PRICE,
          ],
          timeDimensions: this.timeDimension ? [this.timeDimension] : [],
          dimensions: [
            dimensionEnum.SITE,
            dimensionEnum.SITE_NAME,
            dimensionEnum.DATE,
            dimensionEnum.SITE_CURRENCY,
          ],
          filters: [...this.commonFilters],
        };
        // Requete Cube.js
        const resultSet =
          (await TableTools.Helper.doRequest(query, { signal })) || [];

        // Filtre site commun pour la requête running totals
        const siteFilter =
          this.siteValues.length > 0
            ? {
              dimension: dimensionEnum.SITE,
              operator: "equals",
              values: this.siteValues.map((site) => site.id),
            }
            : {
              dimension: dimensionEnum.SITE,
              operator: "equals",
              values: [""],
            };

        // Running totals par (Site, Date, IsTypePurchase, SubType) — une seule requête
        const queryRunningTotals = {
          measures: [
            mesureEnum.VACCOUNTINGRUNNINGTOTALS_RUNNINGTOTALTOKENS,
            mesureEnum.VACCOUNTINGRUNNINGTOTALS_RUNNINGTOTALAMOUNT,
          ],
          timeDimensions: [
            TableTools.Helper.stripTimeDimension(
              this.timeDimension,
              dimensionEnum.VACCOUNTINGRUNNINGTOTALS_DATE
            ),
          ],
          dimensions: [
            dimensionEnum.SITE,
            dimensionEnum.DATE,
            dimensionEnum.VACCOUNTINGRUNNINGTOTALS_ISTYPEPURCHASE,
            dimensionEnum.VACCOUNTINGRUNNINGTOTALS_SUBTYPE,
          ],
          filters: [
            siteFilter,
            {
              or: [
                {
                  dimension:
                    dimensionEnum.VACCOUNTINGRUNNINGTOTALS_ISTYPEPURCHASE,
                  operator: "notEquals",
                  values: ["0"], // Exclure totaux de type dépense
                },
                {
                  dimension: dimensionEnum.VACCOUNTINGRUNNINGTOTALS_SUBTYPE,
                  operator: "notEquals",
                  values: ["-2"], // Exclure tokens qui viennent des Events (pour la retrocompatibilité, à enlever sûrement)
                },
              ],
            },
          ],
        };

        const resultRunningTotals =
          (await TableTools.Helper.doRequest(queryRunningTotals, { signal })) ||
          [];

        // Remplissage des données des tableaux
        this.tablesData.forEach((table) => {
          const tableData = resultSet
            .filter((row) => row[dimensionEnum.SITE] === table.tableHeaderId)
            .map((row) => ({
              [dimensionEnum.DATE]: row[dimensionEnum.DATE],
              [dimensionEnum.SITE_CURRENCY]: row[dimensionEnum.SITE_CURRENCY],
              [mesureEnum.PURGEHISTORY_TOKEN]:
                row[mesureEnum.PURGEHISTORY_TOKEN] || 0,
              [mesureEnum.PURGEHISTORY_FREE_TOKEN]:
                row[mesureEnum.PURGEHISTORY_FREE_TOKEN] || 0,
              [mesureEnum.PURGEHISTORY_TOKEN_PRICE]:
                row[mesureEnum.PURGEHISTORY_TOKEN_PRICE] || 0,
              [mesureEnum.PURGEHISTORY_CA_GENERATED]:
                row[mesureEnum.PURGEHISTORY_TOKEN] *
                row[mesureEnum.PURGEHISTORY_TOKEN_PRICE] || 0,
            }));

          // Pré-filtrer les running totals pour ce site
          const siteRunningTotals = resultRunningTotals.filter(
            (r) => r[dimensionEnum.SITE] === table.tableHeaderId
          );

          tableData.forEach((row) => {
            const date = row[dimensionEnum.DATE];

            const rowsForDate = siteRunningTotals.filter(
              (r) => r[dimensionEnum.DATE] === date
            );

            const sumTokens = (rows) =>
              rows.reduce(
                (sum, r) =>
                  sum +
                  (r[mesureEnum.VACCOUNTINGRUNNINGTOTALS_RUNNINGTOTALTOKENS] ||
                    0),
                0
              );
            const sumAmount = (rows) =>
              rows.reduce(
                (sum, r) =>
                  sum +
                  (r[mesureEnum.VACCOUNTINGRUNNINGTOTALS_RUNNINGTOTALAMOUNT] ||
                    0),
                0
              );

            const purchasedRows = rowsForDate.filter(
              (r) =>
                r[dimensionEnum.VACCOUNTINGRUNNINGTOTALS_ISTYPEPURCHASE] ===
                true
            );
            const spentRows = rowsForDate.filter(
              (r) =>
                r[dimensionEnum.VACCOUNTINGRUNNINGTOTALS_ISTYPEPURCHASE] ===
                false &&
                r[dimensionEnum.VACCOUNTINGRUNNINGTOTALS_SUBTYPE] !== 2
            );
            const expiredRows = rowsForDate.filter(
              (r) =>
                r[dimensionEnum.VACCOUNTINGRUNNINGTOTALS_ISTYPEPURCHASE] ===
                false &&
                r[dimensionEnum.VACCOUNTINGRUNNINGTOTALS_SUBTYPE] === 2
            );

            const purchasedTokens = sumTokens(purchasedRows);
            const spentTokens = sumTokens(spentRows);
            const expiredTokens = sumTokens(expiredRows);

            const purchasedAmount = sumAmount(purchasedRows);
            const spentAmount = sumAmount(spentRows);
            const expiredAmount = sumAmount(expiredRows);

            row[calculatedColumnsEnum.EXPIRED_TOKENS] = expiredTokens;
            row[calculatedColumnsEnum.REMAINING_TOKENS] =
              purchasedTokens - spentTokens - expiredTokens;
            row[calculatedColumnsEnum.TTC_DIFFERENCE] =
              purchasedAmount - spentAmount - expiredAmount;
          });

          table.dataTable = tableData;

          // Récupérer la devise du site
          const siteCurrency =
            table.dataTable.length > 0
              ? table.dataTable[0][dimensionEnum.SITE_CURRENCY]
              : "EUR";

          // Créer les colonnes de mesure avec la devise du site
          table.measureColumnsTables = TableTools.Helper.constructColumnTable(
            [
              mesureEnum.PURGEHISTORY_TOKEN,
              mesureEnum.PURGEHISTORY_FREE_TOKEN,
              mesureEnum.PURGEHISTORY_TOKEN_PRICE,
              mesureEnum.PURGEHISTORY_CA_GENERATED,
              calculatedColumnsEnum.EXPIRED_TOKENS,
              calculatedColumnsEnum.REMAINING_TOKENS,
              calculatedColumnsEnum.TTC_DIFFERENCE,
            ],
            null,
            columnTypeEnum.MEASURE,
            true,
            null,
            false,
            siteCurrency
          );

          // Calculer les totaux pour chaque table - à refaire en dehors de la boucle
          const totalToken = tableData.reduce(
            (sum, row) => sum + (row[mesureEnum.PURGEHISTORY_TOKEN] || 0),
            0
          );
          const totalFreeToken = tableData.reduce(
            (sum, row) => sum + (row[mesureEnum.PURGEHISTORY_FREE_TOKEN] || 0),
            0
          );
          const totalCAGenerated = tableData.reduce(
            (sum, row) =>
              sum + (row[mesureEnum.PURGEHISTORY_CA_GENERATED] || 0),
            0
          );

          table.totalsData = {
            [mesureEnum.PURGEHISTORY_TOKEN]: totalToken,
            [mesureEnum.PURGEHISTORY_FREE_TOKEN]: totalFreeToken,
            [mesureEnum.PURGEHISTORY_CA_GENERATED]: totalCAGenerated,
            [dimensionEnum.SITE_CURRENCY]: siteCurrency,
          };

          // Trier par date
          table.dataTable = TableTools.Helper.sortDatatableByDate(
            table.dataTable,
            dimensionEnum.DATE
          );
        });

        this.dataReadyTables = true;
      } catch (error) {
        if (this.isAbortError(error)) return;
        console.error("Error updating table list:", error);
      }
    },
    downloadPdf(data) {
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_PDF, "_blank");
      }
    },
    downloadExcel(data) {
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_EXCEL, "_blank");
      }
    },
    downloadCsv(data) {
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_CSV, "_blank");
      }
    },
    storeExportData(filename, subtitles) {
      var datasForExport = [];

      this.tablesData.forEach((table, index) => {
        const renderer = this.$refs.tableRenderers?.[index];
        const sortKey = renderer?.sortedColumn || this.defaultSortedColumnTables;

        const data = TableTools.Helper.assembleExportDataTable(
          table.dataTable,
          this.dimensionFields,
          table.measureColumnsTables,
          this.groupHeaders,
          table.tableHeader,
          sortKey,
          null,
          table.totalsData,
          null,
          "table-width-xl export-xxl"
        );
        datasForExport.push(data);
      });

      TableTools.Helper.storeExportData(
        datasForExport,
        this.title,
        subtitles,
        filename, //Titre du fichier
        null,
        true // reduce title for export, to be printed as A3
      );
    },
  },
};
</script>
